/** Mission Control (M6) — typed API bindings. */
import { http } from "@/shared/api/http";

export type PublishingStatus =
  | "queued"
  | "publishing"
  | "published"
  | "failed"
  | "retry_pending"
  | "cancelled";

export type ChannelKey =
  | "instagram" | "facebook" | "linkedin" | "threads" | "x"
  | "tiktok" | "youtube" | "pinterest" | "email" | "sms" | "blog";

export interface ChannelMeta {
  key: ChannelKey;
  label: string;
  icon: string;
  color: string;
}

export interface CalendarEvent {
  id: string;
  permanent_asset_id: string | null;
  asset_id: string;
  campaign_pack_id: string | null;
  workspace_id: string;
  channel: ChannelKey;
  channel_label: string;
  channel_icon: string;
  channel_color: string;
  content_type: string;
  campaign_name: string | null;
  brand_brain_version: string | null;
  creative_brief_id: string | null;
  scheduled_at: string;
  local_preview_at: string;
  workspace_time_zone: string;
  queue_position: number;
  publishing_status: PublishingStatus;
  publishing_status_label: string;
  workspace_color: string | null;
  asset_title: string;
  asset_prefix: string | null;
  recurrence_series_id: string | null;
  ai_recommendation_ref: string | null;
}

export interface ConflictReport {
  id: string;
  type:
    | "duplicate_time_slot" | "channel_congestion" | "campaign_overlap"
    | "missing_approval" | "missing_media" | "missing_required_metadata";
  severity: "info" | "warning" | "error";
  asset_ids: string[];
  message: string;
}

export interface ProgressStage {
  key: "generated" | "reviewed" | "approved" | "scheduled" | "published" | "analytics";
  done: number;
  total: number;
  pct: number;
}

export interface CampaignProgress {
  pack_id: string;
  stages: ProgressStage[];
  overall_pct: number;
  health: "green" | "yellow" | "red";
}

export interface CampaignTimelineEntry {
  pack_id: string;
  objective: string | null;
  campaign_name: string | null;
  preset: string | null;
  total_assets: number;
  progress: CampaignProgress;
  events: CalendarEvent[];
}

export interface Dashboard {
  workspace_id: string;
  scheduled_today: number;
  waiting: number;
  queue_depth: number;
  campaign_completion_avg: number;
  upcoming_deadlines: CalendarEvent[];
  publishing_errors: number;
  workspace_activity_24h: number;
  activity_feed: Array<{
    id: string;
    type: string;
    permanent_asset_id: string | null;
    campaign_pack_id: string | null;
    actor_id: string | null;
    payload: Record<string, unknown>;
    created_at: string;
  }>;
}

export const listChannels = (): Promise<ChannelMeta[]> =>
  http(`/mission-control/channels`);

export const getCalendar = (
  workspaceId: string,
  from?: string,
  to?: string,
): Promise<CalendarEvent[]> => {
  const params = new URLSearchParams();
  if (from) params.set("from_", from);
  if (to) params.set("to", to);
  const q = params.toString();
  return http(`/workspaces/${workspaceId}/calendar${q ? `?${q}` : ""}`);
};

export const getCalendarConflicts = (workspaceId: string): Promise<ConflictReport[]> =>
  http(`/workspaces/${workspaceId}/calendar/conflicts`);

export const getCampaignTimeline = (workspaceId: string): Promise<CampaignTimelineEntry[]> =>
  http(`/workspaces/${workspaceId}/calendar/campaign-timeline`);

export const getDashboard = (workspaceId: string): Promise<Dashboard> =>
  http(`/workspaces/${workspaceId}/calendar/dashboard`);

export const scheduleAsset = (
  workspaceId: string,
  payload: {
    asset_id: string;
    channels: ChannelKey[];
    scheduled_at: string;
    recurrence?: Record<string, unknown>;
  },
): Promise<CalendarEvent[]> =>
  http(`/workspaces/${workspaceId}/schedule`, { method: "POST", body: payload });

export const patchSchedule = (
  workspaceId: string,
  scheduleId: string,
  payload: { scheduled_at?: string; queue_position?: number; channel?: ChannelKey },
): Promise<CalendarEvent> =>
  http(`/workspaces/${workspaceId}/schedule/${scheduleId}`, { method: "PATCH", body: payload });

export const cancelSchedule = (workspaceId: string, scheduleId: string): Promise<CalendarEvent> =>
  http(`/workspaces/${workspaceId}/schedule/${scheduleId}/cancel`, { method: "POST" });

export const publishNow = (workspaceId: string, scheduleId: string): Promise<CalendarEvent> =>
  http(`/workspaces/${workspaceId}/schedule/${scheduleId}/publish-now`, { method: "POST" });

export const PUBLISHING_TONES: Record<PublishingStatus, string> = {
  queued:        "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  publishing:    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 animate-pulse",
  published:     "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  failed:        "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200",
  retry_pending: "bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200",
  cancelled:     "bg-zinc-100 text-zinc-500 dark:bg-zinc-900/60 dark:text-zinc-400",
};
